import { useEffect, useRef } from 'react';

/**
 * useReveal — adds an `.in` class to any descendant with class `.reveal`
 * when it scrolls into view. Returns a ref to attach to the wrapper element.
 * Trigger is once-per-node so animations don't re-run on scroll-up.
 */
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.14 });

    // Observe existing .reveal descendants
    root.querySelectorAll('.reveal').forEach((n) => {
      if (!n.classList.contains('in')) io.observe(n);
    });

    // Catch nodes added later (e.g. when filter/sort re-renders the list)
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.reveal') && !node.classList.contains('in')) {
            io.observe(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.reveal').forEach((n) => {
              if (!n.classList.contains('in')) io.observe(n);
            });
          }
        });
      });
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
  return ref;
}

/**
 * useParallax — translate the returned ref along Y with a slower coefficient
 * than scroll, driven by requestAnimationFrame.
 * Pass `trackRef` to base the parallax progress on a specific ancestor rect,
 * useful when the moving element sits inside `position: sticky` containers.
 */
export function useParallax(strength = 0.15, trackRef) {
  const ref = useRef(null);
  useEffect(() => {
    let raf = null;
    const compute = () => {
      raf = null;
      if (!ref.current) return;
      const el = ref.current;
      const tracker = (trackRef && trackRef.current) || el.parentElement || el;
      const rect = tracker.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const p = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const y = -p * strength * vh;
      el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [strength, trackRef]);
  return ref;
}

/**
 * useDataParallax — Dom-Pérignon-style declarative parallax.
 * Scans the subtree of `rootRef` for elements with `data-parallax-speed="0.15"`
 * (positive = drifts up, negative = drifts down) and applies a scroll-linked
 * translate3d to each. Also honours `data-parallax-clip="inset"` which animates
 * `clip-path: inset(...)` from a shrunken rect to a full reveal as the element
 * enters the viewport — the "clip-path reveal" technique described in the
 * Dom Pérignon parallax analysis.
 *
 * Skipped entirely for users with reduced-motion preference.
 */
export function useDataParallax(rootRef) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const root = rootRef?.current || document.body;
    if (!root) return;

    let raf = null;
    let nodes = [];
    const collect = () => {
      nodes = Array.from(root.querySelectorAll('[data-parallax-speed], [data-parallax-clip]'))
        .map((el) => ({
          el,
          speed: parseFloat(el.getAttribute('data-parallax-speed') || '0'),
          clip: el.getAttribute('data-parallax-clip') || null,
        }));
      nodes.forEach(({ el }) => { el.style.willChange = 'transform, clip-path'; });
    };

    const compute = () => {
      raf = null;
      const vh = window.innerHeight || 1;
      for (const { el, speed, clip } of nodes) {
        const rect = el.getBoundingClientRect();
        // Normalised progress: -1 when just below viewport, 0 when centred,
        // +1 when just above. Same math as useParallax for consistency.
        const p = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        if (speed) {
          const y = -p * speed * vh;
          el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
        }
        if (clip === 'inset') {
          // Convert p (roughly -1 → +1) into a reveal amount 0 → 1 based on
          // vertical position: fully clipped when the element hasn't entered
          // the viewport, fully revealed once its centre passes the middle.
          const enter = Math.min(1, Math.max(0, (vh - rect.top) / vh));
          const reveal = Math.min(1, Math.max(0, enter * 1.35 - 0.15));
          const inset = (1 - reveal) * 12; // percent
          el.style.clipPath = `inset(${inset.toFixed(2)}% ${inset.toFixed(2)}% ${inset.toFixed(2)}% ${inset.toFixed(2)}% round 2px)`;
        }
      }
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };

    collect();
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('lenis:scroll', onScroll);
    window.addEventListener('resize', onScroll);

    // Re-collect when the tree changes (e.g. filters, tab switches).
    const mo = new MutationObserver(() => { collect(); onScroll(); });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('lenis:scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [rootRef]);
}
